import React, { useEffect, useState, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Package as PackageIcon, Plus, Pencil, Trash2, Loader2, ToggleLeft, ToggleRight } from 'lucide-react';
import { toast } from 'sonner';
import {
  getMyPackages, createPackage, updatePackage, deletePackage,
} from '../lib/api';
import { CATEGORY_NAMES, formatCurrency } from '../lib/utils';

const CATEGORY_OPTIONS = [
  { value: 'personal_training', label: CATEGORY_NAMES.personal_training },
  { value: 'self_defense', label: CATEGORY_NAMES.self_defense },
  { value: 'jiujitsu', label: CATEGORY_NAMES.jiujitsu },
];

const blankForm = {
  name: '',
  category: 'personal_training',
  session_count: 5,
  total_price: '',
  description: '',
};

const TrainerPackagesPanel = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(blankForm);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getMyPackages();
      setItems(res.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const openCreate = () => {
    setEditingId(null);
    setForm(blankForm);
    setEditorOpen(true);
  };

  const openEdit = (pkg) => {
    setEditingId(pkg.id);
    setForm({
      name: pkg.name || '',
      category: pkg.category || 'personal_training',
      session_count: pkg.session_count || 5,
      total_price: String(pkg.total_price ?? ''),
      description: pkg.description || '',
    });
    setEditorOpen(true);
  };

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    const name = form.name.trim();
    const count = Number(form.session_count);
    const price = Number(form.total_price);
    if (!name) { toast.error('Package name is required'); return; }
    if (!Number.isFinite(count) || count < 2) { toast.error('Sessions must be at least 2'); return; }
    if (!Number.isFinite(price) || price <= 0) { toast.error('Price must be greater than 0'); return; }
    const perSessionRate = price / count;
    if (perSessionRate < 40) {
      toast.error('Minimum session rate is $40.');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name,
        category: form.category,
        session_count: Math.round(count),
        total_price: Math.round(price * 100) / 100,
        description: form.description?.trim() || null,
      };
      if (editingId) {
        await updatePackage(editingId, payload);
        toast.success('Package updated');
      } else {
        await createPackage(payload);
        toast.success('Package created');
      }
      setEditorOpen(false);
      await refresh();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Failed to save package';
      toast.error(typeof msg === 'string' ? msg : 'Failed to save package');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (pkg) => {
    setBusyId(pkg.id);
    try {
      await updatePackage(pkg.id, { is_active: !pkg.is_active });
      await refresh();
    } catch (e) {
      toast.error('Could not update package status');
    } finally {
      setBusyId(null);
    }
  };

  const removePackage = async (pkg) => {
    if (!window.confirm(`Delete the "${pkg.name}" package? This cannot be undone.`)) return;
    setBusyId(pkg.id);
    try {
      await deletePackage(pkg.id);
      toast.success('Package deleted');
      await refresh();
    } catch (e) {
      toast.error('Could not delete package');
    } finally {
      setBusyId(null);
    }
  };

  const perSession = (() => {
    const c = Number(form.session_count);
    const p = Number(form.total_price);
    if (!Number.isFinite(c) || !Number.isFinite(p) || c <= 0) return null;
    return p / c;
  })();
  const perSessionBelowMin = perSession !== null && perSession > 0 && perSession < 40;

  return (
    <div className="bg-[#18181B] border border-zinc-800 rounded-lg p-6" data-testid="trainer-packages-panel">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <PackageIcon className="w-5 h-5 text-[#C0A062]" />
            Session Packages
          </h2>
          <p className="text-xs text-zinc-400 mt-1">
            Sell bundles upfront (e.g. 5 sessions for $600). Clients buy once and book each session against the credit.
          </p>
        </div>
        <Button
          onClick={openCreate}
          className="bg-[#C0A062] text-black hover:bg-[#a98a52] font-semibold"
          data-testid="add-package-btn"
        >
          <Plus className="w-4 h-4 mr-1" /> New package
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-zinc-500 text-sm py-6">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : items.length === 0 ? (
        <p className="text-sm text-zinc-500 py-6 text-center" data-testid="empty-packages">
          No packages yet. Create one to start selling bundles.
        </p>
      ) : (
        <div className="grid gap-3" data-testid="packages-list">
          {items.map((p) => {
            const per = p.total_price / Math.max(p.session_count, 1);
            return (
              <div
                key={p.id}
                className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 rounded-lg border ${
                  p.is_active ? 'border-zinc-700 bg-[#0d0d10]' : 'border-zinc-800 bg-[#0d0d10]/60 opacity-70'
                }`}
                data-testid={`package-row-${p.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-white font-semibold">{p.name}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-300">
                      {CATEGORY_NAMES[p.category] || p.category}
                    </span>
                    {!p.is_active && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-zinc-700 text-zinc-300">Hidden</span>
                    )}
                  </div>
                  <p className="text-sm text-zinc-400 mt-1">
                    {p.session_count} sessions · {formatCurrency(p.total_price)} total ·{' '}
                    <span className="text-zinc-500">{formatCurrency(per)}/session</span>
                  </p>
                  {p.description && (
                    <p className="text-xs text-zinc-500 mt-1 line-clamp-2">{p.description}</p>
                  )}
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleActive(p)}
                    disabled={busyId === p.id}
                    className="text-zinc-400 hover:text-white"
                    data-testid={`toggle-package-${p.id}`}
                    title={p.is_active ? 'Hide from public profile' : 'Show on public profile'}
                  >
                    {p.is_active ? <ToggleRight className="w-5 h-5 text-emerald-400" /> : <ToggleLeft className="w-5 h-5" />}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openEdit(p)}
                    disabled={busyId === p.id}
                    className="text-zinc-400 hover:text-white"
                    data-testid={`edit-package-${p.id}`}
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removePackage(p)}
                    disabled={busyId === p.id}
                    className="text-zinc-400 hover:text-red-400"
                    data-testid={`delete-package-${p.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent
          className="bg-[#18181B] border-zinc-800 text-white max-w-md"
          data-testid="package-editor-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingId ? 'Edit package' : 'Create a package'}
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Bundles are charged upfront. Clients redeem one credit each time they book.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid gap-2">
              <Label className="text-zinc-300">Package name</Label>
              <Input
                value={form.name}
                onChange={(e) => setField('name', e.target.value)}
                placeholder="e.g. 5-Session Starter Pack"
                className="bg-[#09090B] border-zinc-800 text-white"
                data-testid="package-name-input"
              />
            </div>

            <div className="grid gap-2">
              <Label className="text-zinc-300">Service</Label>
              <Select value={form.category} onValueChange={(v) => setField('category', v)}>
                <SelectTrigger className="bg-[#09090B] border-zinc-800 text-white" data-testid="package-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#18181B] border-zinc-800 text-white">
                  {CATEGORY_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-2">
                <Label className="text-zinc-300">Sessions</Label>
                <Input
                  type="number"
                  min="2"
                  value={form.session_count}
                  onChange={(e) => setField('session_count', e.target.value)}
                  className="bg-[#09090B] border-zinc-800 text-white"
                  data-testid="package-sessions-input"
                />
              </div>
              <div className="grid gap-2">
                <Label className="text-zinc-300">Total price (USD)</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    value={form.total_price}
                    onChange={(e) => setField('total_price', e.target.value)}
                    placeholder="600"
                    className="bg-[#09090B] border-zinc-800 text-white pl-7"
                    data-testid="package-price-input"
                  />
                </div>
              </div>
            </div>

            {perSession !== null && (
              <p className="text-xs text-zinc-500" data-testid="package-per-session">
                Clients pay <span className="text-[#C0A062] font-semibold">{formatCurrency(perSession)}</span> per session.
              </p>
            )}

            <div className="grid gap-2">
              <Label className="text-zinc-300">Description (optional)</Label>
              <Textarea
                rows={3}
                value={form.description}
                onChange={(e) => setField('description', e.target.value)}
                placeholder="What's included? Any conditions?"
                className="bg-[#09090B] border-zinc-800 text-white"
                data-testid="package-description-input"
              />
            </div>
          </div>

          <DialogFooter className="mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setEditorOpen(false)}
              disabled={saving}
              className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={save}
              disabled={saving || perSessionBelowMin}
              className="bg-[#C0A062] text-black hover:bg-[#a98a52]"
              data-testid="package-save-btn"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editingId ? 'Save changes' : 'Create package'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TrainerPackagesPanel;
